import { Button } from '@/components/ui/button';
import { useState } from 'react';
import type { FormEvent } from 'react';
import {
    Link,
    Navigate,
    useLocation,
    useNavigate,
} from 'react-router-dom';

import { Page } from '@/components/layout/page';
import { Input } from '@/components/ui/field';

import {
    Alert,
    Card,
    CardBody,
    Container,
    Section,
} from '@/components/ui/primitives';

import { useSession } from '@/hooks/use-session';

import {
    fieldErrors,
    isApiError,
    presentError,
} from '@/lib/errors';

import { paths } from '@/lib/paths';

/**
 * Connexion — `POST /auth/login`.
 *
 * Il n'y a pas de page d'inscription sur ce site.
 * Un compte est créé après un paiement réussi.
 *
 * Après une connexion réussie, le visiteur est renvoyé
 * vers la page qu'il souhaitait consulter.
 */
export default function LoginPage() {
    const {
        login,
        isAuthenticated,
    } = useSession();

    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const redirectTo =
        (
            location.state as
                | {
                      from?: string;
                  }
                | null
        )?.from ?? paths.account;

    const fields = fieldErrors(error);

    if (isAuthenticated) {
        return (
            <Navigate
                to={redirectTo}
                replace
            />
        );
    }

    async function handleSubmit(
        event: FormEvent<HTMLFormElement>,
    ): Promise<void> {
        event.preventDefault();

        setSubmitting(true);
        setError(null);

        try {
            await login({
                email,
                password,
            });

            navigate(
                redirectTo,
                {
                    replace: true,
                },
            );
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    const presentation =
        error !== null
            ? presentError(error)
            : null;

    return (
        <Page title="Connexion">
            <Section>
                <Container className="max-w-md space-y-6">

                    {/* En-tête */}
                    <div className="text-center">
                        <h1 className="font-display text-3xl tracking-tight">
                            Se connecter
                        </h1>

                        <p className="text-ink-soft mt-2 text-sm">
                            Retrouvez votre accès et vos informations
                            de facturation.
                        </p>
                    </div>

                    {/* Formulaire */}
                    <Card>
                        <CardBody>
                            <form
                                onSubmit={handleSubmit}
                                className="space-y-5"
                                noValidate
                            >
                                <Input
                                    label="Adresse email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    error={fields.email}
                                    onChange={(event) =>
                                        setEmail(
                                            event.target.value,
                                        )
                                    }
                                />

                                <Input
                                    label="Mot de passe"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    error={fields.password}
                                    onChange={(event) =>
                                        setPassword(
                                            event.target.value,
                                        )
                                    }
                                />

                                {error !== null &&
                                    Object.keys(fields).length === 0 &&
                                    presentation && (
                                        <Alert
                                            tone="critical"
                                            title={
                                                presentation.message
                                            }
                                        >
                                            <p>
                                                {
                                                    presentation.hint
                                                }
                                            </p>

                                            {isApiError(
                                                error,
                                                'invalid_credentials',
                                            ) && (
                                                <p className="mt-2">
                                                    <Link
                                                        to={
                                                            paths.forgotPassword
                                                        }
                                                        className="underline"
                                                    >
                                                        Mot de passe
                                                        oublié ?
                                                    </Link>
                                                </p>
                                            )}
                                        </Alert>
                                    )}

                                <Button
                                    type="submit"
                                    size="lg"
                                    loading={submitting}
                                    disabled={
                                        email.trim() === '' ||
                                        password === ''
                                    }
                                    className="
                                        w-full
                                        bg-[#b20303]
                                        text-white
                                        shadow-md
                                        transition-all
                                        duration-300
                                        hover:-translate-y-0.5
                                        hover:bg-[#950202]
                                        hover:shadow-lg
                                    "
                                >
                                    Se connecter
                                </Button>
                            </form>
                        </CardBody>
                    </Card>

                    {/* Liens secondaires */}
                    <div className="text-ink-muted space-y-2 text-center text-sm">

                        <p>
                            <Link
                                to={paths.forgotPassword}
                                className="text-brand-700 hover:underline"
                            >
                                Mot de passe oublié ou jamais défini ?
                            </Link>
                        </p>

                        <p>
                            Pas encore de compte ?{' '}

                            <Link
                                to={paths.pricing}
                                className="text-brand-700 hover:underline"
                            >
                                Il est créé automatiquement lors de
                                votre premier achat.
                            </Link>
                        </p>

                    </div>

                </Container>
            </Section>
        </Page>
    );
}